import { AfterViewInit, ChangeDetectorRef, Component, HostListener, Inject, OnInit, Renderer2, ViewChild, ViewContainerRef } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CSSPlugin, gsap } from 'gsap';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import dayjs from 'dayjs';

import { environment } from '../environments/environment';

import { AuthService } from './auth/services/auth.service';
import { GamePeriodService } from './game/services/game-period.service';
import { GameService } from './game/services/game.service';
import { LayoutService } from './layout/services/layout.service';
import { ActiveRouteName } from './routing/services/active-route-name.service';
import { PreviousRouteService } from './routing/services/previous-route.service';
import { DialogService } from './shared/dialog/services/dialog.service';
import { AnchorService } from './shared/services/anchor.service';
import { ThemeService } from './shared/theme/services/theme.service';
import { TranslationService } from './translation/services/translation.service';
import { AppService } from './app.service';

gsap.registerPlugin(CSSPlugin);

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit {

  @ViewChild('dialogContainer', {read: ViewContainerRef}) public dialogComponentContainer: ViewContainerRef;

  public authenticated: Observable<boolean>;
  public loading: boolean = true;
  public ready: boolean = true;

  public theme: string;

  public backgroundVideoEnabled: boolean;
  public loaded: boolean;

  private readonly device: string = 'desktop';
  private linkThemeElement: HTMLElement;
  private linkLangElement: HTMLElement;
  private linkThemeLegacyElement: HTMLElement;

  private faviconElement: HTMLElement;
  private favicon16Element: HTMLElement;
  private favicon76Element: HTMLElement;
  private favicon152Element: HTMLElement;

  private routeDataSubscription: Subscription;
  private subscription: Subscription;

  /**
   * Constructor
   *
   * @param {Router} router
   * @param {ActivatedRoute} route
   * @param {AppService} appService
   * @param {AuthService} authService
   * @param {TranslateService} translate
   * @param {TranslationService} translation
   * @param {GameService} gameService
   * @param {ThemeService} themeService
   * @param {LayoutService} layoutService
   * @param {GamePeriodService} gamePeriodService
   * @param {PreviousRouteService} previousRouteService
   * @param {ChangeDetectorRef} cdRef
   * @param {ActivatedRoute} activatedRoute
   * @param {Meta} meta
   * @param {ActiveRouteName} activeRouteName
   * @param {AnchorService} anchorService
   * @param {Renderer2} renderer
   * @param {TranslationService} translationService
   * @param {DialogService} dialogService
   * @param {Document} document
   */
  public constructor(
    private router: Router,
    private route: ActivatedRoute,
    private appService: AppService,
    private authService: AuthService,
    private translate: TranslateService,
    private translation: TranslationService,
    private gameService: GameService,
    private themeService: ThemeService,
    private layoutService: LayoutService,
    private gamePeriodService: GamePeriodService,
    private previousRouteService: PreviousRouteService,
    private cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private meta: Meta,
    private activeRouteName: ActiveRouteName,
    private anchorService: AnchorService,
    private renderer: Renderer2,
    private translationService: TranslationService,
    private dialogService: DialogService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.device = this.meta.getTag('name=application-device').content;

    this.authenticated = this.authService.getPlayerAuthenticationStatus();
    this.subscription = new Subscription();

    this.backgroundVideoEnabled = true;

    translate.setTranslation(appService.language, translation.translations);
    dayjs.locale(appService.language);
    this.translate.use(appService.language);
  }

  /**
   * Listens to clicks and parses links as routerLinks
   * @param {Event} event
   */
  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event) {
    this.anchorService.interceptClick(event);
  }

  /**
   * Initialize the App Component
   *
   * @return void
   */
  public async ngOnInit(): Promise<void> {

    if (environment.production) {
      this.linkLangElement = document.getElementById('lang-theme-stylesheet');
      this.linkThemeElement = document.getElementById('theme-stylesheet');
      this.linkThemeLegacyElement = document.getElementById('theme-stylesheet-legacy');
      this.faviconElement = document.getElementById('theme-favicon');
      this.favicon16Element = document.getElementById('apple-touch-icon-16');
      this.favicon76Element = document.getElementById('apple-touch-icon-76');
      this.favicon152Element = document.getElementById('apple-touch-icon-152');

      const metaTagCustomTheme: HTMLMetaElement = this.meta.getTag('name=custom-theme');
      if (metaTagCustomTheme) {
        this.themeService.customTheme = metaTagCustomTheme.content.toLowerCase();
        this.subscription.add(
          this.themeService.getCustomThemeChange().subscribe(
            (customTheme: string) => {
              customTheme
                ? this.themeService.createStyleLinkInHead(customTheme)
                : this.themeService.removeStyleLinkInHead(customTheme);
            }),
        );
      }

      this.subscription.add(
        this.themeService.getThemeChange().subscribe(
          (newTheme: string) => {
            if (newTheme) {
              this.translationService.loadTranslationsForTheme(newTheme);

              const oldTheme: string = this.theme;
              oldTheme
                ? this.document.body.classList.replace('theme-' + oldTheme, 'theme-' + newTheme)
                : this.document.body.classList.add('theme-' + newTheme);

              this.updateTheme(newTheme);

              this.theme = newTheme;
            }
          },
        ),
      );

      this.subscription.add(
        this.themeService.getWebAccessibilityChange().subscribe(
          (webAccessibility: boolean) => {
            this.setWebAccessibility(webAccessibility);
          },
        ),
      );

    } else {

      // When working on development, also update the body class!
      this.subscription.add(
        this.themeService.getThemeChange().subscribe(
          (newTheme: string) => {
            if (newTheme && newTheme !== this.theme) {
              this.translationService.loadTranslationsForTheme(newTheme);

              const oldTheme: string = this.theme;
              oldTheme
                ? this.document.body.classList.replace('theme-' + oldTheme, 'theme-' + newTheme)
                : this.document.body.classList.add('theme-' + newTheme);

              this.theme = newTheme;
            }
          },
        ),
      );

      this.subscription.add(
          this.appService.getLanguageChanged().subscribe(
            (language) => {
              console.log('language changed', language)
            },
          )
      );

      const metaTagCustomTheme: HTMLMetaElement = this.meta.getTag('name=custom-theme');
      if (metaTagCustomTheme) {
        this.themeService.customTheme = metaTagCustomTheme.content.toLowerCase();
        this.subscription.add(
          this.themeService.getCustomThemeChange().subscribe(
            (customTheme: string) => {
              customTheme
                ? this.themeService.createStyleLinkInHead(customTheme)
                : this.themeService.removeStyleLinkInHead(customTheme);
            }),
        );
      }

      if (this.meta.getTag('name=custom-theme') !== null) {
        this.themeService.customThemeChangeSubject.next(this.themeService.customTheme);
      }
    }

    this.routeDataSubscription = this.mergeRouteData().subscribe(
      (data) => {
        // Sets the section name based on definitions in the routing files
        this.layoutService.changeSectionClass.next(data.hasOwnProperty('section') ? data.section : '');
        this.layoutService.updateBackground();
      },
    );
  }

  /**
   * After view init
   *
   * @return void
   */
  public ngAfterViewInit(): void {
    this.dialogService.componentContainer = this.dialogComponentContainer;
  }

  /**
   * Merge the route data
   *
   * @return Observable
   */
  private mergeRouteData(): Observable<any> {
    return this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        let route: ActivatedRoute = this.route.firstChild;
        let child = route;
        let page: string;

        while (child) {
          if (child.firstChild) {
            child = child.firstChild;
            route = child;

            const routeData = (route.data as BehaviorSubject<any>).getValue();
            if (routeData && routeData.page) {
               page = routeData.page;
             }
          } else {
            child = null;
          }
        }

        if (page) {
          this.sendAnalyticsPageView(page);
        }

        return route;
      }),
      mergeMap((route: ActivatedRoute) => route.data),
    );
  }

  /**
   * Send Analytics Page View
   *
   * @param {string} page
   *
   * @return void
   */
  private sendAnalyticsPageView(page: string): void {
    // google analytics
    if (typeof (<any>window).ga !== 'undefined') {
      (<any>window).ga('set', 'page', page);
      (<any>window).ga('send', 'pageview');
    }
  }

  /**
   * Update the Link Elements when the theme is being changed
   *
   * @return void
   * @param updatedTheme
   */
  private updateTheme(updatedTheme: string): void {

    // Set the new used theme.
    this.theme = updatedTheme;

    const version = this.meta.getTag('name=application-version').content;

    const themeHref = this.themeService.assetsDir + this.theme + '/' + this.device + '/css/' + this.device + '.css?v=' + version;

    this.linkThemeElement.setAttribute('href', themeHref);

    const langHref = this.themeService.assetsDir + this.theme + '/desktop/css/lang/' + this.appService.language + '/desktop.css?v=' + version;
    this.linkLangElement.setAttribute('href', langHref);

    if (this.linkThemeLegacyElement !== null) {
      const themeLegacyHref = this.themeService.assetsDir + this.theme + '/' + this.device + '/css/' + this.device + '-legacy.css?v=' + version;
      this.linkThemeLegacyElement.setAttribute('href', themeLegacyHref);
    }

    const faviconHref = this.themeService.assetsDir + this.theme + '/shared/images/favicon.ico';
    this.faviconElement.setAttribute('href', faviconHref);

    const favicon16Href = this.themeService.assetsDir + this.theme + '/shared/images/favicon-16x16.png';
    this.favicon16Element.setAttribute('href', favicon16Href);

    const favicon76Href = this.themeService.assetsDir + this.theme + '/shared/images/favicon-76x76.png';
    this.favicon76Element.setAttribute('href', favicon76Href);

    const favicon152Href = this.themeService.assetsDir + this.theme + '/shared/images/favicon-152x152.png';
    this.favicon152Element.setAttribute('href', favicon152Href);
  }

  /**
   * Load the accessibility style sheet
   *
   * @return void
   */
  private setWebAccessibility(webAccessibility): void {

    if (this.themeService.theme === undefined || this.themeService.theme === null) {
      return;
    }

    let stylesheet = this.device + '.css';

    if (webAccessibility === true) {
      stylesheet = this.device + '-accessibility.css';
    }

    const version = this.meta.getTag('name=application-version').content;

    const themeHref = this.themeService.assetsDir
      + this.themeService.theme + '/'
      + this.device + '/css/'
      + stylesheet + '?v=' + version;

    this.linkThemeElement.setAttribute('href', themeHref);
  }
}
